import { useCallback, useEffect, useRef, useSyncExternalStore } from 'react'

/**
 * Hook that ensures a loading state is displayed for a minimum duration.
 * Useful for preventing flickering when loading completes very quickly.
 *
 * @param isLoading - The current loading state
 * @param minDisplayTimeMs - Minimum time in milliseconds to display the loader (default: 1000ms)
 * @returns Whether the loader should be shown (true if loading or within minimum display time)
 */
export function useMinimumDisplayTime(isLoading: boolean, minDisplayTimeMs = 1000): boolean {
  // Use refs to track internal state without triggering re-renders
  const loadingStartTimeRef = useRef<number | null>(null)
  const isWaitingForMinTimeRef = useRef(false)
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const subscribersRef = useRef(new Set<() => void>())

  // Subscribe function for useSyncExternalStore
  const subscribe = useCallback((callback: () => void) => {
    subscribersRef.current.add(callback)
    return () => {
      subscribersRef.current.delete(callback)
    }
  }, [])

  // Notify all subscribers when state changes
  const notifySubscribers = useCallback(() => {
    subscribersRef.current.forEach((callback) => callback())
  }, [])

  // Get the current snapshot of whether we should show the loader
  const getSnapshot = useCallback(() => {
    return isLoading || isWaitingForMinTimeRef.current
  }, [isLoading])

  // Handle loading state changes
  useEffect(() => {
    if (isLoading) {
      // Start loading - record the start time
      if (loadingStartTimeRef.current === null) {
        loadingStartTimeRef.current = Date.now()
        isWaitingForMinTimeRef.current = false
      }
      return
    }

    // Loading finished - check if we need minimum display time
    if (loadingStartTimeRef.current === null) {
      return
    }

    const elapsed = Date.now() - loadingStartTimeRef.current

    if (elapsed >= minDisplayTimeMs) {
      // Already displayed for at least minimum time, reset immediately
      loadingStartTimeRef.current = null
      isWaitingForMinTimeRef.current = false
      return
    }

    // Wait until minimum display time is reached
    const remainingTime = minDisplayTimeMs - elapsed
    isWaitingForMinTimeRef.current = true

    timeoutRef.current = setTimeout(() => {
      isWaitingForMinTimeRef.current = false
      loadingStartTimeRef.current = null
      notifySubscribers()
    }, remainingTime)

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
        timeoutRef.current = null
      }
    }
  }, [isLoading, minDisplayTimeMs, notifySubscribers])

  // Use useSyncExternalStore to subscribe to our ref-based state
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot)
}
